/**
 * L'écran Affectation — qui conduit quoi, et avec quelle remorque.
 *
 * Conforme à la maquette validée le 03/09/2026, en deux vues que l'utilisateur
 * a demandées l'une après l'autre :
 *
 *   - **VIGNETTES** : une miniature par véhicule, puis le grand schéma de celui
 *     qu'on choisit. Chaque pièce attelée porte sa date et une croix pour la
 *     détacher ; chaque place vide se remplit depuis la colonne des libres ;
 *   - **LISTE** : le même parc en tableau, avec des cases à cocher. Deux
 *     véhicules cochés font apparaître la bande d'échange — « on ajoute la
 *     possibilité de faire un changement rapide entre deux ressources ».
 *
 * UNE SEULE DIVERGENCE AVEC LA MAQUETTE, et elle est délibérée. La maquette
 * montrait trois colonnes de libres — véhicules, remorques, chauffeurs — pour
 * composer une combinaison à partir de rien. Il n'y a plus de colonne des
 * véhicules libres : le véhicule se choisit par sa vignette, et le schéma
 * affiche SON attelage. C'est ce que l'utilisateur a demandé en voyant la
 * première maquette — « il n'y a aucune notion des affectations en cours » — et
 * une colonne qui redemanderait le véhicule déjà choisi ferait deux chemins
 * pour la même chose.
 *
 * Découpe habituelle : `VueAffectation` dessine, `EcranAffectation` tient
 * l'état et appelle. Le calcul est dans `affectation.ts`, en fonctions pures.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  changerAttelage,
  echangerAttelages,
  lireAffectations,
  lireIndisponibilites,
  lireParc,
  rangerAttelages,
  supprimerAttelage,
  type EchecApi,
  type FiltreAffectations,
} from './api.js';
import {
  AUCUN,
  INCHANGE,
  attelageDe,
  attelages,
  aujourdhui,
  avertissementDit,
  corpsDeChangement,
  disponibilites,
  ecritureDite,
  enFrancais,
  finProposee,
  lendemain,
  libres,
  noteDeRessource,
  refusChangement,
  refusFin,
  periodeDite,
  refusEchange,
  resumeEmpeches,
  resumeLibres,
  signeEcriture,
  titreEchange,
  veille,
  type Affectation,
  type Attelage,
  type Indisponibilite,
  type PlanRangement,
  type Quoi,
  type SaisieChangement,
} from './affectation.js';
import { nommer, type LigneParc } from './parc.js';
import { PARC_CHAUFFEURS, PARC_REMORQUES, PARC_VEHICULES } from './parcs.js';
import { SchemaAttelage, VignetteAttelage } from './SchemaAttelage.js';

type Vue = 'vignettes' | 'liste';

type EtatParc =
  | { readonly nom: 'chargement' }
  | {
      readonly nom: 'pret';
      readonly vehicules: readonly LigneParc[];
      readonly remorques: readonly LigneParc[];
      readonly chauffeurs: readonly LigneParc[];
      readonly affectations: readonly Affectation[];
      readonly indisponibilites: readonly Indisponibilite[];
    }
  | { readonly nom: 'panne' };

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cet attelage n’existe plus. Il a pu être défait entre-temps.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Cette valeur est déjà prise.',
  invalide:
    'Une date n’est pas acceptée : le dernier jour d’attelage ne peut pas précéder le premier.',
  conflit:
    'Cette ressource est déjà attelée sur tout ou partie de cette période. Décrochez-la d’abord, ou choisissez d’autres dates.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Impossible : quelque chose s’y rattache encore.',
  // Les quatre refus du planning. Ils n'arrivent pas sur cet écran — la carte
  // doit rester exhaustive, c'est ce qui garantit qu'aucun refus de l'API ne
  // s'affichera un jour sous forme de code brut.
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape : rien à poser sur un camion.',
  etape_validee:
    'Une étape de cet ordre est déjà validée : ses dates ne se déplacent plus, et son camion ne se retire plus.',
  jour_sans_etape: 'Cet ordre n’a aucune étape ce jour-là. Rechargez l’écran.',
  // Ne vient jamais d'ici. La carte reste exhaustive, comme les autres.
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
  // LES SIX REFUS DE LA FACTURATION, ajoutés le 09/09/2026. Ils n'arrivent pas
  // sur cet écran — la carte reste exhaustive, c'est ce qui garantit qu'aucun
  // refus de l'API ne s'affichera un jour sous forme de code brut.
  litige_ouvert: 'Cet ordre porte un litige ouvert : il ne se facture pas.',
  deja_facture: 'Cet ordre est déjà porté par une facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise: 'Cette pièce est émise : elle se corrige par un avoir.',
  facture_brouillon: 'Un brouillon ne s’envoie pas : validez-le d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas.',
  // AJOUTÉ LE 09/09/2026. Un ordre parti en facturation ne se modifie plus :
  // il faut le retirer de la facturation d'abord.
  ordre_en_facturation:
    'Cet ordre est parti en facturation : retirez-le de la facturation avant de le modifier.',
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Chacun dit la SUITE,
  // pas seulement le refus : c'est ce qui distingue un état d'une saisie fautive.
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

export interface ProprietesVueAffectation {
  readonly etat: EtatParc;
  readonly vue: Vue;
  readonly quandVue: (vue: Vue) => void;
  /** La date à laquelle on regarde, et à laquelle un changement prend effet. */
  readonly jour: string;
  readonly quandJour: (jour: string) => void;
  readonly vehiculeChoisi: string | null;
  readonly quandVehicule: (id: string) => void;
  readonly coches: readonly string[];
  readonly quandCocher: (id: string, coche: boolean) => void;
  readonly quandToutDecocher: () => void;
  readonly quandEchanger: (quoi: Quoi) => void;
  readonly quandAtteler: (genre: 'chauffeur' | 'remorque', ressourceId: string) => void;
  readonly quandDetacher: (affectationId: string) => void;
  /**
   * Ouvre la fenêtre « Changer l'attelage » d'un véhicule — lot 94. Depuis le
   * bouton de la ligne comme depuis une case vide : c'est la même fenêtre, qui
   * range les autres attelages au lieu de refuser.
   */
  readonly quandChanger: (vehicule: LigneParc) => void;
  /** Corriger les dates d'une période de l'historique — lot 94. */
  readonly quandModifierPeriode: (ligne: Affectation) => void;
  /** Supprimer une période saisie par erreur — lot 94. */
  readonly quandSupprimerPeriode: (ligne: Affectation) => void;
  readonly envoi: boolean;
  readonly refus: string | null;
  /** Ce qui vient d'être fait, dit une fois. */
  readonly confirmation: string | null;
  readonly quandConfirmationLue: () => void;
  /**
   * L'HISTORIQUE, qui répond à « qui conduisait ce camion le 12 août ».
   *
   * Il est demandé à l'API plutôt que filtré sur ce qu'on a déjà : la liste
   * chargée pour dessiner le parc ne porte que les 500 dernières périodes, et
   * un historique cherché sur une fenêtre ancienne n'y serait pas.
   */
  readonly historique: Historique;
  readonly quandHistorique: (filtre: Partial<FiltreHistorique>) => void;
}

/** Ce qu'on cherche : une ressource, une fenêtre. */
export interface FiltreHistorique {
  /** « v:<id> », « c:<id> », « r:<id> », ou '' pour tout le parc. */
  readonly ressource: string;
  readonly du: string;
  readonly au: string;
}

export type Historique =
  | { readonly nom: 'ferme' }
  | { readonly nom: 'chargement'; readonly filtre: FiltreHistorique }
  | {
      readonly nom: 'pret';
      readonly filtre: FiltreHistorique;
      readonly lignes: readonly Affectation[];
    }
  | { readonly nom: 'panne'; readonly filtre: FiltreHistorique };

export function VueAffectation({
  etat,
  vue,
  quandVue,
  jour,
  quandJour,
  vehiculeChoisi,
  quandVehicule,
  coches,
  quandCocher,
  quandToutDecocher,
  quandEchanger,
  quandAtteler,
  quandDetacher,
  quandChanger,
  quandModifierPeriode,
  quandSupprimerPeriode,
  envoi,
  refus,
  confirmation,
  quandConfirmationLue,
  historique,
  quandHistorique,
}: ProprietesVueAffectation) {
  if (etat.nom === 'chargement') {
    return (
      <div className="corps">
        <section className="liste">
          <p className="attente">Chargement du parc…</p>
        </section>
      </div>
    );
  }

  if (etat.nom === 'panne') {
    return (
      <div className="corps">
        <section className="liste">
          <div className="vide">
            <p>Stafa ne répond pas. Vérifiez votre connexion et réessayez.</p>
          </div>
        </section>
      </div>
    );
  }

  const liste = attelages(etat.vehicules, etat.affectations, jour);
  const choisi =
    vehiculeChoisi === null ? undefined : attelageDe(liste, vehiculeChoisi);

  const etatsRemorques = disponibilites(
    etat.remorques,
    etat.affectations,
    etat.indisponibilites,
    'remorque_id',
    jour,
  );
  const etatsChauffeurs = disponibilites(
    etat.chauffeurs,
    etat.affectations,
    etat.indisponibilites,
    'chauffeur_id',
    jour,
  );

  const cochesAttelages = coches
    .map((id) => attelageDe(liste, id))
    .filter((a): a is Attelage => a !== undefined);
  const [premier, second] = cochesAttelages;

  return (
    <div className="corps">
      <section className="liste">
        <div className="barre-outils barre-affectation">
          <label className="champ-date">
            <span className="etiquette">
              Attelages au{' '}
              <span className="sourdine">· pour consulter une autre date</span>
            </span>
            <input
              className="champ"
              type="date"
              value={jour}
              disabled={envoi}
              onChange={(e) => quandJour(e.target.value)}
            />
          </label>
          <div className="bascule-vue" role="group" aria-label="Forme d’affichage">
            <button
              className={vue === 'liste' ? 'bouton' : 'bouton bouton-discret'}
              type="button"
              aria-pressed={vue === 'liste'}
              onClick={() => quandVue('liste')}
            >
              Tableau
            </button>
            <button
              className={vue === 'vignettes' ? 'bouton' : 'bouton bouton-discret'}
              type="button"
              aria-pressed={vue === 'vignettes'}
              onClick={() => quandVue('vignettes')}
            >
              Vignettes
            </button>
          </div>
        </div>

        {refus !== null && <p className="bandeau">{refus}</p>}

        {confirmation !== null && (
          <p className="bandeau bandeau-fait" role="status">
            {confirmation}
            <button
              className="fermer-bandeau"
              type="button"
              aria-label="Fermer"
              onClick={quandConfirmationLue}
            >
              ×
            </button>
          </p>
        )}

        {liste.length === 0 && (
          <div className="vide">
            <p>Aucun véhicule actif dans le parc.</p>
          </div>
        )}

        {vue === 'vignettes' && liste.length > 0 && (
          <VueVignettes
            liste={liste}
            choisi={choisi}
            quandVehicule={quandVehicule}
            jour={jour}
            etatsRemorques={etatsRemorques}
            etatsChauffeurs={etatsChauffeurs}
            quandAtteler={quandAtteler}
            quandDetacher={quandDetacher}
            envoi={envoi}
          />
        )}

        {vue === 'liste' && liste.length > 0 && (
          <VueListe
            liste={liste}
            coches={coches}
            quandCocher={quandCocher}
            quandToutDecocher={quandToutDecocher}
            quandEchanger={quandEchanger}
            quandDetacher={quandDetacher}
            quandChanger={quandChanger}
            premier={premier}
            second={second}
            jour={jour}
            envoi={envoi}
          />
        )}

        <Historique
          etat={historique}
          quandFiltre={quandHistorique}
          vehicules={etat.vehicules}
          remorques={etat.remorques}
          chauffeurs={etat.chauffeurs}
          envoi={envoi}
          quandModifier={quandModifierPeriode}
          quandSupprimer={quandSupprimerPeriode}
        />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'historique
// ---------------------------------------------------------------------------

/**
 * « Qui conduisait ce camion le 12 août ? »
 *
 * Replié par défaut : c'est une recherche, pas un tableau de bord, et déplié il
 * pousserait le schéma hors de l'écran.
 *
 * **Une fenêtre garde ce qu'elle CHEVAUCHE**, non ce qui y tient entièrement —
 * une période à cheval sur le 1er avril concerne bien avril. C'est l'API qui en
 * décide ; l'écran ne fait que passer les deux bornes.
 */
function Historique({
  etat,
  quandFiltre,
  vehicules,
  remorques,
  chauffeurs,
  envoi,
  quandModifier,
  quandSupprimer,
}: {
  readonly etat: Historique;
  readonly quandFiltre: (filtre: Partial<FiltreHistorique>) => void;
  readonly vehicules: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly envoi: boolean;
  readonly quandModifier: (ligne: Affectation) => void;
  readonly quandSupprimer: (ligne: Affectation) => void;
}) {
  if (etat.nom === 'ferme') {
    return (
      <div className="cadre-historique">
        <button
          className="bouton bouton-discret"
          type="button"
          onClick={() => quandFiltre({})}
        >
          Historique des attelages
        </button>
      </div>
    );
  }

  const { filtre } = etat;

  return (
    <div className="cadre-historique ouvert">
      <div className="colonne-titre">Historique des attelages</div>

      <div className="barre-outils barre-affectation">
        <label className="champ-date">
          <span className="etiquette">Ressource</span>
          <select
            className="champ"
            value={filtre.ressource}
            onChange={(e) => quandFiltre({ ressource: e.target.value })}
          >
            <option value="">Tout le parc</option>
            <optgroup label="Véhicules">
              {vehicules.map((v) => (
                <option value={`v:${v.id}`} key={v.id}>
                  {nommer(v, PARC_VEHICULES)}
                </option>
              ))}
            </optgroup>
            <optgroup label="Remorques">
              {remorques.map((r) => (
                <option value={`r:${r.id}`} key={r.id}>
                  {nommer(r, PARC_REMORQUES)}
                </option>
              ))}
            </optgroup>
            <optgroup label="Chauffeurs">
              {chauffeurs.map((c) => (
                <option value={`c:${c.id}`} key={c.id}>
                  {nommer(c, PARC_CHAUFFEURS)}
                </option>
              ))}
            </optgroup>
          </select>
        </label>

        <label className="champ-date">
          <span className="etiquette">Entre le</span>
          <input
            className="champ"
            type="date"
            value={filtre.du}
            onChange={(e) => quandFiltre({ du: e.target.value })}
          />
        </label>

        <label className="champ-date">
          <span className="etiquette">et le</span>
          <input
            className="champ"
            type="date"
            value={filtre.au}
            onChange={(e) => quandFiltre({ au: e.target.value })}
          />
        </label>
      </div>

      {etat.nom === 'chargement' && <p className="attente">Recherche…</p>}

      {etat.nom === 'panne' && (
        <p className="empechement">
          Stafa ne répond pas. Vérifiez votre connexion et réessayez.
        </p>
      )}

      {etat.nom === 'pret' && etat.lignes.length === 0 && (
        <p className="empechement">Aucun attelage sur cette période.</p>
      )}

      {etat.nom === 'pret' && etat.lignes.length > 0 && (
        <>
          <p className="decompte">
            {etat.lignes.length} période{etat.lignes.length > 1 ? 's' : ''} trouvée
            {etat.lignes.length > 1 ? 's' : ''}
          </p>
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col">Véhicule</th>
                  <th scope="col" className="col-large">
                    Genre
                  </th>
                  <th scope="col">Ressource</th>
                  <th scope="col">Période</th>
                  <th scope="col">
                    <span className="sourdine">—</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {etat.lignes.map((ligne) => (
                  <tr className="rangee" key={ligne.id}>
                    <td className="code">{ligne.vehicule}</td>
                    <td className="sourdine col-large">{ligne.genre}</td>
                    <td className={ligne.genre === 'remorque' ? 'code' : undefined}>
                      {ligne.ressource}
                    </td>
                    <td>{periodeDite(ligne)}</td>
                    {/*
                      LOT 94 : une période de l'historique se CORRIGE ou se
                      SUPPRIME d'ici. La route de suppression existait depuis
                      le 03/09, sans rien à l'écran pour l'appeler.
                    */}
                    <td>
                      <button
                        className="action-cellule"
                        type="button"
                        disabled={envoi}
                        onClick={() => quandModifier(ligne)}
                      >
                        Modifier les dates
                      </button>
                      <button
                        className="action-cellule risque"
                        type="button"
                        disabled={envoi}
                        onClick={() => quandSupprimer(ligne)}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// La vue en vignettes
// ---------------------------------------------------------------------------

function VueVignettes({
  liste,
  choisi,
  quandVehicule,
  jour,
  etatsRemorques,
  etatsChauffeurs,
  quandAtteler,
  quandDetacher,
  envoi,
}: {
  readonly liste: readonly Attelage[];
  readonly choisi: Attelage | undefined;
  readonly quandVehicule: (id: string) => void;
  readonly jour: string;
  readonly etatsRemorques: ReturnType<typeof disponibilites>;
  readonly etatsChauffeurs: ReturnType<typeof disponibilites>;
  readonly quandAtteler: (genre: 'chauffeur' | 'remorque', ressourceId: string) => void;
  readonly quandDetacher: (affectationId: string) => void;
  readonly envoi: boolean;
}) {
  const remorquesLibres = libres(etatsRemorques);
  const chauffeursLibres = libres(etatsChauffeurs);

  return (
    <>
      <div className="bande-vignettes" role="tablist" aria-label="Le parc">
        {liste.map((attelage) => {
          const actif = choisi?.vehicule.id === attelage.vehicule.id;
          return (
            <button
              className={actif ? 'vignette choisie' : 'vignette'}
              type="button"
              role="tab"
              aria-selected={actif}
              key={attelage.vehicule.id}
              onClick={() => quandVehicule(attelage.vehicule.id)}
            >
              <VignetteAttelage
                vehicule={nommer(attelage.vehicule, PARC_VEHICULES)}
                remorque={attelage.remorque?.ressource ?? null}
                chauffeur={attelage.chauffeur?.ressource ?? null}
              />
              <span className="vignette-nom">
                {nommer(attelage.vehicule, PARC_VEHICULES)}
              </span>
              {/*
                Les TROIS lignes, toujours. La version précédente n'écrivait le
                chauffeur et la remorque que lorsqu'ils MANQUAIENT : une vignette
                complète ne montrait que l'immatriculation du tracteur, et il
                fallait cliquer pour savoir qui conduisait. Relevé par
                l'utilisateur le 04/09/2026 — « les vignettes n'affichent que le
                tracteur affecté ». La date est celle du plus RÉCENT des deux
                attelages, comme la colonne « Attelé depuis » de la vue liste :
                deux calculs différents pour la même chose finiraient par
                diverger.
              */}
              <span
                className={
                  attelage.chauffeur === undefined
                    ? 'vignette-ligne vignette-manque'
                    : 'vignette-ligne'
                }
              >
                {attelage.chauffeur?.ressource ?? 'sans chauffeur'}
              </span>
              <span
                className={
                  attelage.remorque === undefined
                    ? 'vignette-ligne vignette-manque'
                    : 'vignette-ligne'
                }
              >
                {attelage.remorque?.ressource ?? 'sans remorque'}
              </span>
              {(() => {
                const debuts = [attelage.chauffeur?.du, attelage.remorque?.du].filter(
                  (d): d is string => d !== undefined,
                );
                if (debuts.length === 0) return null;
                return (
                  <span className="vignette-depuis">
                    depuis le {enFrancais(debuts.sort().at(-1) as string)}
                  </span>
                );
              })()}
            </button>
          );
        })}
      </div>

      {choisi !== undefined && (
        <>
          <SchemaAttelage
            vehicule={nommer(choisi.vehicule, PARC_VEHICULES)}
            remorque={choisi.remorque?.ressource ?? null}
            chauffeur={choisi.chauffeur?.ressource ?? null}
            depuisRemorque={enFrancais(choisi.remorque?.du ?? null)}
            depuisChauffeur={enFrancais(choisi.chauffeur?.du ?? null)}
            {...(choisi.remorque === undefined || envoi
              ? {}
              : {
                  quandDetacherRemorque: () =>
                    quandDetacher((choisi.remorque as Affectation).id),
                })}
            {...(choisi.chauffeur === undefined || envoi
              ? {}
              : {
                  quandDetacherChauffeur: () =>
                    quandDetacher((choisi.chauffeur as Affectation).id),
                })}
          />

          {(choisi.remorque === undefined || choisi.chauffeur === undefined) && (
            <>
              <p className="decompte">
                {resumeLibres(remorquesLibres.length, chauffeursLibres.length, jour)}
                {(() => {
                  const dits = [
                    resumeEmpeches(etatsRemorques, (r) => nommer(r, PARC_REMORQUES)),
                    resumeEmpeches(etatsChauffeurs, (c) => nommer(c, PARC_CHAUFFEURS)),
                  ].filter((d) => d !== '');
                  return dits.length === 0 ? '' : ` — ${dits.join(', ')}`;
                })()}
              </p>

              <div className="colonnes-libres">
                {choisi.remorque === undefined && (
                  <ColonneLibres
                    titre="Remorques libres"
                    ressources={remorquesLibres}
                    parc={PARC_REMORQUES}
                    envoi={envoi}
                    quandChoisir={(id) => quandAtteler('remorque', id)}
                  />
                )}
                {choisi.chauffeur === undefined && (
                  <ColonneLibres
                    titre="Chauffeurs libres"
                    ressources={chauffeursLibres}
                    parc={PARC_CHAUFFEURS}
                    envoi={envoi}
                    quandChoisir={(id) => quandAtteler('chauffeur', id)}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

function ColonneLibres({
  titre,
  ressources,
  parc,
  envoi,
  quandChoisir,
}: {
  readonly titre: string;
  readonly ressources: readonly LigneParc[];
  readonly parc: typeof PARC_REMORQUES;
  readonly envoi: boolean;
  readonly quandChoisir: (id: string) => void;
}) {
  return (
    <div className="colonne-libres">
      <div className="colonne-titre">{titre}</div>
      {ressources.length === 0 ? (
        <p className="empechement">Rien de libre à cette date.</p>
      ) : (
        ressources.map((ressource) => (
          <button
            className="ligne-libre"
            type="button"
            key={ressource.id}
            disabled={envoi}
            onClick={() => quandChoisir(ressource.id)}
          >
            {nommer(ressource, parc)}
          </button>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// La vue en liste, et l'échange
// ---------------------------------------------------------------------------

function VueListe({
  liste,
  coches,
  quandCocher,
  quandToutDecocher,
  quandEchanger,
  quandDetacher,
  quandChanger,
  premier,
  second,
  jour,
  envoi,
}: {
  readonly liste: readonly Attelage[];
  readonly coches: readonly string[];
  readonly quandCocher: (id: string, coche: boolean) => void;
  readonly quandToutDecocher: () => void;
  readonly quandEchanger: (quoi: Quoi) => void;
  readonly quandDetacher: (affectationId: string) => void;
  readonly quandChanger: (vehicule: LigneParc) => void;
  readonly premier: Attelage | undefined;
  readonly second: Attelage | undefined;
  readonly jour: string;
  readonly envoi: boolean;
}) {
  return (
    <>
      <p className="decompte">
        {liste.length} véhicule{liste.length > 1 ? 's' : ''}
        {coches.length > 0 &&
          ` — ${coches.length} coché${coches.length > 1 ? 's' : ''}`}
      </p>

      {coches.length === 2 && premier !== undefined && second !== undefined && (
        <BandeEchange
          premier={premier}
          second={second}
          jour={jour}
          envoi={envoi}
          quandEchanger={quandEchanger}
          quandToutDecocher={quandToutDecocher}
        />
      )}

      <div className="tableau-cadre">
        <table>
          <thead>
            <tr>
              <th scope="col" className="col-case">
                <span className="sourdine">—</span>
              </th>
              <th scope="col">Véhicule</th>
              <th scope="col" className="col-large">
                Type
              </th>
              <th scope="col">Chauffeur</th>
              <th scope="col">Remorque</th>
              <th scope="col" className="col-large">
                Attelé depuis
              </th>
              <th scope="col">
                <span className="sourdine">—</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {liste.map((attelage) => {
              const id = attelage.vehicule.id;
              const coche = coches.includes(id);
              // La date affichée est celle du plus RÉCENT des deux attelages :
              // c'est le dernier changement, et c'est ce qu'on cherche.
              const debuts = [attelage.chauffeur?.du, attelage.remorque?.du].filter(
                (d): d is string => d !== undefined,
              );
              const depuis =
                debuts.length === 0 ? null : enFrancais(debuts.sort().at(-1) as string);

              return (
                <tr className={coche ? 'rangee choisie' : 'rangee'} key={id}>
                  <td className="col-case">
                    <input
                      type="checkbox"
                      checked={coche}
                      disabled={envoi || (!coche && coches.length >= 2)}
                      aria-label={`Choisir ${nommer(attelage.vehicule, PARC_VEHICULES)}`}
                      onChange={(e) => quandCocher(id, e.target.checked)}
                    />
                  </td>
                  <td className="code">{nommer(attelage.vehicule, PARC_VEHICULES)}</td>
                  <td className="col-large">
                    {(attelage.vehicule['type'] as string | null) ?? (
                      <span className="sourdine">non renseigné</span>
                    )}
                  </td>
                  {/*
                    L'action est SOUS la ressource, et toujours là. Avant le
                    04/09/2026, « atteler » n'existait que dans les vignettes et
                    seulement si la place était vide : un camion complet
                    n'offrait plus rien, et rien ne laissait deviner qu'il
                    fallait d'abord détacher.
                  */}
                  <td>
                    {attelage.chauffeur === undefined ? (
                      <>
                        <span className="sourdine">sans chauffeur</span>
                        <button
                          className="action-cellule"
                          type="button"
                          disabled={envoi}
                          onClick={() => quandChanger(attelage.vehicule)}
                        >
                          Atteler un chauffeur
                        </button>
                      </>
                    ) : (
                      <>
                        <span>{attelage.chauffeur.ressource}</span>
                        <button
                          className="action-cellule risque"
                          type="button"
                          disabled={envoi}
                          onClick={() =>
                            quandDetacher((attelage.chauffeur as Affectation).id)
                          }
                        >
                          Décrocher
                        </button>
                      </>
                    )}
                  </td>
                  <td>
                    {attelage.remorque === undefined ? (
                      <>
                        <span className="sourdine">sans remorque</span>
                        <button
                          className="action-cellule"
                          type="button"
                          disabled={envoi}
                          onClick={() => quandChanger(attelage.vehicule)}
                        >
                          Atteler une remorque
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="code">{attelage.remorque.ressource}</span>
                        <button
                          className="action-cellule risque"
                          type="button"
                          disabled={envoi}
                          onClick={() =>
                            quandDetacher((attelage.remorque as Affectation).id)
                          }
                        >
                          Décrocher
                        </button>
                      </>
                    )}
                  </td>
                  {depuis === null ? (
                    <td className="sourdine col-large">—</td>
                  ) : (
                    <td className="col-large">{depuis}</td>
                  )}
                  <td>
                    <button
                      className="bouton bouton-discret"
                      type="button"
                      disabled={envoi}
                      onClick={() => quandChanger(attelage.vehicule)}
                    >
                      Changer l’attelage
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function BandeEchange({
  premier,
  second,
  jour,
  envoi,
  quandEchanger,
  quandToutDecocher,
}: {
  readonly premier: Attelage;
  readonly second: Attelage;
  readonly jour: string;
  readonly envoi: boolean;
  readonly quandEchanger: (quoi: Quoi) => void;
  readonly quandToutDecocher: () => void;
}) {
  const boutons: readonly { readonly quoi: Quoi; readonly nom: string }[] = [
    { quoi: 'remorques', nom: 'Échanger les remorques' },
    { quoi: 'chauffeurs', nom: 'Échanger les chauffeurs' },
    { quoi: 'les_deux', nom: 'Échanger les deux' },
  ];

  return (
    <div className="bande-echange">
      <span className="echange-titre">
        {titreEchange(premier, second, (v) => nommer(v, PARC_VEHICULES))}
      </span>
      <span className="echange-date">
        À partir du {enFrancais(jour) ?? jour}
        <span className="sourdine">
          {' '}
          · les attelages en place seront clos le {enFrancais(veille(jour)) ?? ''}
        </span>
      </span>
      <div className="actions">
        {boutons.map(({ quoi, nom }, rang) => {
          const refus = refusEchange(premier, second, quoi, jour);
          return (
            <button
              className={rang === 0 ? 'bouton' : 'bouton bouton-discret'}
              type="button"
              key={quoi}
              disabled={envoi || refus !== null}
              title={refus ?? undefined}
              onClick={() => quandEchanger(quoi)}
            >
              {nom}
            </button>
          );
        })}
        <button
          className="bouton bouton-discret"
          type="button"
          disabled={envoi}
          onClick={quandToutDecocher}
        >
          Tout décocher
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesAffectation {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

export function EcranAffectation({ jeton, quandAccesPerdu }: ProprietesAffectation) {
  const [etat, setEtat] = useState<EtatParc>({ nom: 'chargement' });
  // Le TABLEAU par défaut depuis le 04/09/2026. Les vignettes montraient un
  // seul ensemble à la fois sur toute la largeur de l'écran ; l'exploitant en a
  // sept et veut les voir tous le matin. Les vignettes restent, en second.
  const [vue, setVue] = useState<Vue>('liste');
  const [jour, setJour] = useState(aujourdhui());
  const [vehiculeChoisi, setVehiculeChoisi] = useState<string | null>(null);
  const [coches, setCoches] = useState<readonly string[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  /**
   * Ce qu'on vient de faire, dit une fois.
   *
   * Posé le 04/09/2026 : décrocher au jour même ne changeait RIEN à l'écran —
   * la fin est ce jour-là inclus, donc l'attelage le couvre encore — et on
   * cliquait sans rien voir se passer. Le bandeau dit ce qui a eu lieu et à
   * partir de quand ça se verra.
   */
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [historique, setHistorique] = useState<Historique>({ nom: 'ferme' });
  /**
   * Le décrochage en cours, s'il y en a un.
   *
   * Décrocher DEMANDE la date depuis le 04/09/2026, à la demande de
   * l'utilisateur. Auparavant, l'écran posait la fin à la veille du jour
   * affiché en haut, sans rien demander : décrocher le jour même une remorque
   * attelée le jour même était refusé par la base, et le seul contournement
   * était d'aller changer une date qui, elle, ne sert qu'à consulter.
   */
  const [decrochage, setDecrochage] = useState<Decrochage | null>(null);
  /**
   * LE CHANGEMENT EN COURS DE SAISIE — lot 94, 17/09/2026.
   *
   * Remplace la boîte « Atteler » des lots précédents, qui ne proposait que
   * les ressources libres au jour choisi et laissait la base refuser tout ce
   * qui recouvrait un attelage déjà déclaré — y compris un attelage PLUS TARD.
   * C'était le blocage : « si j'ai déclaré un attelage, je ne peux pas
   * déclarer un attelage à une date inférieure ».
   */
  const [changement, setChangement] = useState<Changement | null>(null);
  /** La correction de dates d'une période de l'historique — lot 94. */
  const [correction, setCorrection] = useState<Correction | null>(null);
  /** La suppression demandée, en attente de confirmation — lot 94. */
  const [suppression, setSuppression] = useState<Affectation | null>(null);
  /**
   * Le filtre de l'historique ouvert, pour le relire après une écriture. Une
   * période corrigée qui resterait affichée avec ses anciennes dates ferait
   * croire que la correction n'a pas pris.
   */
  const filtreOuvert = useRef<FiltreHistorique | null>(null);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * Tout est chargé d'un coup, à l'ouverture : les trois parcs, les attelages
   * et les arrêts. Sans filtre de date sur les affectations — on change de date
   * en tapant, et redemander l'API à chaque frappe ferait clignoter l'écran
   * pour un calcul que le navigateur fait en une milliseconde.
   */
  const charger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;

    const [v, r, c, a, i] = await Promise.all([
      lireParc(j, PARC_VEHICULES),
      lireParc(j, PARC_REMORQUES),
      lireParc(j, PARC_CHAUFFEURS),
      lireAffectations(j),
      lireIndisponibilites(j),
    ]);

    if (
      v.etat !== 'ok' ||
      r.etat !== 'ok' ||
      c.etat !== 'ok' ||
      a.etat !== 'ok' ||
      i.etat !== 'ok'
    ) {
      for (const resultat of [v, r, c, a, i]) {
        if (resultat.etat === 'echec' && resultat.cause === 'acces') quandAccesPerdu();
      }
      setEtat({ nom: 'panne' });
      return;
    }

    setEtat({
      nom: 'pret',
      vehicules: v.valeur,
      remorques: r.valeur,
      chauffeurs: c.valeur,
      affectations: a.valeur,
      indisponibilites: i.valeur,
    });
    setVehiculeChoisi((actuel) => {
      if (actuel !== null && v.valeur.some((ligne) => ligne.id === actuel)) {
        return actuel;
      }
      return v.valeur.find((ligne) => ligne.actif)?.id ?? null;
    });
  }, [avecJeton, quandAccesPerdu]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** L'historique, lu pour un filtre. */
  const lireHistorique = useCallback(
    (filtre: FiltreHistorique) => {
      filtreOuvert.current = filtre;
      setHistorique({ nom: 'chargement', filtre });
      void (async () => {
        const j = await avecJeton();
        if (j === null) return;
        const lu = await lireAffectations(j, filtreApi(filtre));
        if (lu.etat === 'ok') {
          setHistorique({ nom: 'pret', filtre, lignes: lu.valeur });
        } else {
          if (lu.cause === 'acces') quandAccesPerdu();
          setHistorique({ nom: 'panne', filtre });
        }
      })();
    },
    [avecJeton, quandAccesPerdu],
  );

  /** Une écriture, puis un rechargement : la base a le dernier mot. */
  const ecrire = (
    action: (j: string) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>,
    dit?: string,
  ) => {
    setEnvoi(true);
    setRefus(null);
    setConfirmation(null);

    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setEnvoi(false);
        return;
      }
      const resultat = await action(j);
      if (resultat.etat === 'ok') {
        await charger();
        if (filtreOuvert.current !== null) lireHistorique(filtreOuvert.current);
        setCoches([]);
        setEnvoi(false);
        if (dit !== undefined) setConfirmation(dit);
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      // Chaque cause a son message, et un seul. La version précédente affichait
      // le message de CONFLIT pour tout `invalide` : une fin antérieure au début
      // s'annonçait donc comme un recouvrement de périodes. Constaté sur le
      // poste le 04/09/2026.
      setRefus(REFUS[resultat.cause]);
      setEnvoi(false);
    })();
  };

  const apercuChangement = useApercu(
    changement === null || refusChangement(changement) !== null
      ? null
      : corpsDeChangement(changement, true),
    changement === null ? null : refusChangement(changement),
    avecJeton,
  );
  const apercuCorrection = useApercu(
    correction === null || refusCorrection(correction) !== null
      ? null
      : corpsDeCorrection(correction, true),
    correction === null ? null : refusCorrection(correction),
    avecJeton,
  );

  const ecran = (
    <VueAffectation
      etat={etat}
      vue={vue}
      quandVue={(v) => {
        setVue(v);
        setCoches([]);
      }}
      jour={jour}
      quandJour={(j) => {
        // Un champ de date vidé rendrait la chaîne vide, et tout deviendrait
        // « non couvert ». On garde alors la date précédente.
        if (j !== '') setJour(j);
      }}
      confirmation={confirmation}
      quandConfirmationLue={() => setConfirmation(null)}
      quandChanger={(vehicule) => {
        setRefus(null);
        setConfirmation(null);
        setChangement(nouveauChangement(vehicule, jour));
      }}
      quandModifierPeriode={(ligne) => {
        setRefus(null);
        setConfirmation(null);
        setCorrection({ ligne, du: ligne.du, au: ligne.au ?? '' });
      }}
      quandSupprimerPeriode={(ligne) => {
        setRefus(null);
        setConfirmation(null);
        setSuppression(ligne);
      }}
      vehiculeChoisi={vehiculeChoisi}
      quandVehicule={setVehiculeChoisi}
      coches={coches}
      quandCocher={(id, coche) =>
        setCoches((actuels) =>
          coche
            ? actuels.includes(id)
              ? actuels
              : [...actuels, id].slice(0, 2)
            : actuels.filter((c) => c !== id),
        )
      }
      quandToutDecocher={() => setCoches([])}
      quandEchanger={(quoi) => {
        const [a, b] = coches;
        if (a === undefined || b === undefined) return;
        ecrire((j) =>
          echangerAttelages(j, { vehicule_a: a, vehicule_b: b, quoi, du: jour }),
        );
      }}
      quandAtteler={(genre, ressourceId) => {
        // LOT 94 : un clic dans la colonne des libres n'écrit plus d'emblée.
        // Il ouvre la fenêtre, la ressource déjà choisie : le plan s'y montre
        // avant d'enregistrer, comme partout ailleurs.
        if (vehiculeChoisi === null || etat.nom !== 'pret') return;
        const vehicule = etat.vehicules.find((v) => v.id === vehiculeChoisi);
        if (vehicule === undefined) return;
        setRefus(null);
        setConfirmation(null);
        setChangement({
          ...nouveauChangement(vehicule, jour),
          [genre]: ressourceId,
        });
      }}
      quandDetacher={(affectationId) => {
        // On n'écrit plus rien ici : on OUVRE la demande de date. C'est elle
        // qui écrira, avec la borne saisie.
        if (etat.nom !== 'pret') return;
        const ligne = etat.affectations.find((a) => a.id === affectationId);
        if (ligne === undefined) return;
        setRefus(null);
        setDecrochage({
          id: ligne.id,
          du: ligne.du,
          genre: ligne.genre,
          ressource: ligne.ressource,
          vehicule: ligne.vehicule,
          au: finProposee(ligne.du, jour),
        });
      }}
      envoi={envoi}
      refus={refus}
      historique={historique}
      quandHistorique={(change) => {
        // Ouvrir sans filtre : les trois derniers mois, tout le parc. Une
        // fenêtre par défaut plutôt qu'aucune — sans elle, la première réponse
        // serait les 500 dernières périodes du parc entier, ce qui n'est une
        // réponse à rien.
        const depart: FiltreHistorique =
          historique.nom === 'ferme'
            ? { ressource: '', du: ilYaTroisMois(jour), au: jour }
            : historique.filtre;
        lireHistorique({ ...depart, ...change });
      }}
    />
  );

  return (
    <>
      {ecran}
      {changement !== null && etat.nom === 'pret' && (
        <BoiteChangement
          changement={changement}
          chauffeurs={optionsRessources(
            etat.chauffeurs,
            PARC_CHAUFFEURS,
            'chauffeur_id',
            changement,
            etat,
          )}
          remorques={optionsRessources(
            etat.remorques,
            PARC_REMORQUES,
            'remorque_id',
            changement,
            etat,
          )}
          apercu={apercuChangement}
          envoi={envoi}
          quandChanger={(champs) => setChangement({ ...changement, ...champs })}
          quandFermer={() => setChangement(null)}
          quandValider={() => {
            if (apercuChangement.nom !== 'pret') return;
            const saisie = changement;
            const nombre = apercuChangement.plan.ecritures.length;
            setChangement(null);
            ecrire(
              (j) => rangerAttelages(j, corpsDeChangement(saisie, false)),
              `${nombre} changement${nombre > 1 ? 's' : ''} enregistré${
                nombre > 1 ? 's' : ''
              } ensemble sur ${saisie.vehicule}.`,
            );
          }}
        />
      )}
      {correction !== null && (
        <BoiteCorrection
          correction={correction}
          apercu={apercuCorrection}
          envoi={envoi}
          quandChanger={(champs) => setCorrection({ ...correction, ...champs })}
          quandFermer={() => setCorrection(null)}
          quandValider={() => {
            if (apercuCorrection.nom !== 'pret') return;
            const corps = corpsDeCorrection(correction, false);
            setCorrection(null);
            ecrire((j) => rangerAttelages(j, corps), 'Période corrigée.');
          }}
        />
      )}
      {suppression !== null && (
        <BoiteSuppression
          ligne={suppression}
          envoi={envoi}
          quandFermer={() => setSuppression(null)}
          quandValider={() => {
            const { id, ressource, vehicule } = suppression;
            setSuppression(null);
            ecrire(
              (j) => supprimerAttelage(j, id),
              `Période supprimée : ${ressource} sur ${vehicule}.`,
            );
          }}
        />
      )}
      {decrochage !== null && (
        <BoiteDecrochage
          decrochage={decrochage}
          envoi={envoi}
          quandAu={(au) => setDecrochage({ ...decrochage, au })}
          quandFermer={() => setDecrochage(null)}
          quandValider={() => {
            const { id, du, au, ressource, genre } = decrochage;
            if (refusFin(du, au) !== null) return;
            setDecrochage(null);
            ecrire(
              (j) => changerAttelage(j, id, { au }),
              `${ressource} est décroché${genre === 'remorque' ? 'e' : ''} : ${
                genre === 'remorque' ? 'elle' : 'il'
              } redevient libre le ${enFrancais(lendemain(au)) ?? lendemain(au)}.`,
            );
          }}
        />
      )}
    </>
  );
}

/** Ce que la demande de date tient le temps d'être remplie. */
interface Decrochage {
  readonly id: string;
  /** Le premier jour de l'attelage : la fin ne peut pas le précéder. */
  readonly du: string;
  readonly genre: 'chauffeur' | 'remorque';
  /** Le nom du chauffeur, ou l'immatriculation de la remorque. */
  readonly ressource: string;
  readonly vehicule: string;
  /** Le dernier jour d'attelage, tel qu'il est saisi. */
  readonly au: string;
}

// ---------------------------------------------------------------------------
// Changer un attelage sans blocage — lot 94
// ---------------------------------------------------------------------------

/** Ce que la fenêtre « Changer l'attelage » tient. */
export interface Changement extends SaisieChangement {
  /** L'immatriculation du véhicule, pour le titre et le bandeau. */
  readonly vehicule: string;
}

/** Une fenêtre vide pour ce véhicule, à la date affichée en haut. */
function nouveauChangement(vehicule: LigneParc, jour: string): Changement {
  return {
    vehiculeId: vehicule.id,
    vehicule: nommer(vehicule, PARC_VEHICULES),
    du: jour,
    au: '',
    chauffeur: INCHANGE,
    remorque: INCHANGE,
  };
}

/** La correction des dates d'une période de l'historique. */
export interface Correction {
  readonly ligne: Affectation;
  readonly du: string;
  /** Vide = sans fin. */
  readonly au: string;
}

function refusCorrection(correction: Correction): string | null {
  if (correction.du === '') return 'Indiquez le premier jour.';
  if (correction.au !== '' && correction.au < correction.du) {
    return 'Le dernier jour ne peut pas précéder le premier.';
  }
  if (
    correction.du === correction.ligne.du &&
    correction.au === (correction.ligne.au ?? '')
  ) {
    return 'Changez au moins une des deux dates.';
  }
  return null;
}

function corpsDeCorrection(
  correction: Correction,
  simuler: boolean,
): Record<string, unknown> {
  return {
    remplace_id: correction.ligne.id,
    du: correction.du,
    ...(correction.au === '' ? {} : { au: correction.au }),
    simuler,
  };
}

/** Une ressource dans une liste de la fenêtre. */
export interface OptionRessource {
  readonly id: string;
  readonly libelle: string;
}

/**
 * Les ressources ACTIVES, TOUTES — lot 94. La boîte précédente n'offrait que
 * les libres, et c'est ce qui empêchait de choisir un chauffeur déjà attelé
 * ailleurs. Chacune porte ce qu'on sait d'elle à la date choisie.
 */
function optionsRessources(
  ressources: readonly LigneParc[],
  parc: typeof PARC_CHAUFFEURS,
  colonne: 'chauffeur_id' | 'remorque_id',
  changement: Changement,
  etat: {
    readonly affectations: readonly Affectation[];
    readonly indisponibilites: readonly Indisponibilite[];
  },
): readonly OptionRessource[] {
  return ressources
    .filter((r) => r.actif)
    .map((r) => {
      const note =
        changement.du === ''
          ? null
          : noteDeRessource(
              r.id,
              colonne,
              changement.vehiculeId,
              etat.affectations,
              etat.indisponibilites,
              changement.du,
            );
      const nom = nommer(r, parc);
      return { id: r.id, libelle: note === null ? nom : `${nom} — ${note}` };
    });
}

/** L'aperçu du plan, tel que l'API l'a simulé. */
export type Apercu =
  | { readonly nom: 'vide'; readonly message: string | null }
  | { readonly nom: 'calcul' }
  | { readonly nom: 'pret'; readonly plan: PlanRangement }
  | { readonly nom: 'refus'; readonly message: string };

/**
 * DEMANDE LA SIMULATION À CHAQUE CHANGEMENT DE SAISIE.
 *
 * La réponse d'une saisie périmée est ÉCARTÉE : on compare le corps qui l'a
 * demandée à celui du moment. Sans cela, deux dates tapées vite pourraient
 * afficher le plan de la première sous la seconde — et la personne validerait
 * sur un aperçu qui ne correspond pas à ce qui partira.
 */
function useApercu(
  corps: Record<string, unknown> | null,
  refus: string | null,
  avecJeton: () => Promise<string | null>,
): Apercu {
  const cle = corps === null ? null : JSON.stringify(corps);
  const [lu, setLu] = useState<{
    readonly cle: string;
    readonly apercu: Apercu;
  } | null>(null);

  useEffect(() => {
    if (cle === null) return undefined;
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      let apercu: Apercu;
      try {
        const reponse = await rangerAttelages(
          j,
          JSON.parse(cle) as Record<string, unknown>,
        );
        apercu =
          reponse.etat === 'ok'
            ? { nom: 'pret', plan: reponse.valeur }
            : { nom: 'refus', message: REFUS[reponse.cause] };
      } catch {
        apercu = { nom: 'refus', message: REFUS.service };
      }
      if (!abandonne) setLu({ cle, apercu });
    })();
    return () => {
      abandonne = true;
    };
  }, [cle, avecJeton]);

  if (cle === null) return { nom: 'vide', message: refus };
  if (lu === null || lu.cle !== cle) return { nom: 'calcul' };
  return lu.apercu;
}

/** Peut-on enregistrer ce plan ? */
function planValable(apercu: Apercu): boolean {
  return (
    apercu.nom === 'pret' &&
    !apercu.plan.deja_en_place &&
    apercu.plan.ecritures.length > 0
  );
}

/** Ce qui sera écrit, et ce qu'il faut savoir avant de valider. */
function BlocApercu({ apercu }: { readonly apercu: Apercu }) {
  return (
    <div className="apercu-rangement" aria-live="polite">
      <p className="etiquette-boite">Ce qui sera enregistré</p>
      {apercu.nom === 'vide' && (
        <p className="aide-boite">{apercu.message ?? 'Remplissez la saisie.'}</p>
      )}
      {apercu.nom === 'calcul' && <p className="aide-boite">Calcul…</p>}
      {apercu.nom === 'refus' && <p className="faute-boite">{apercu.message}</p>}
      {apercu.nom === 'pret' && apercu.plan.deja_en_place && (
        <p className="aide-boite">
          Rien à changer : c’est déjà l’attelage sur toute la période.
        </p>
      )}
      {apercu.nom === 'pret' && !apercu.plan.deja_en_place && (
        <>
          <ul className="liste-rangement">
            {apercu.plan.ecritures.map((e, rang) => (
              <li className={`rangement-${e.action}`} key={rang}>
                <span className="signe-rangement" aria-hidden="true">
                  {signeEcriture(e)}
                </span>
                {ecritureDite(e)}
              </li>
            ))}
          </ul>
          {apercu.plan.avertissements.map((a, rang) => (
            <p className="bandeau bandeau-attente avert-rangement" key={rang}>
              {avertissementDit(a)}
            </p>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * LA FENÊTRE « CHANGER L'ATTELAGE » — lot 94, maquette validée par Mohamed le
 * 17/09/2026.
 *
 * On ne remplit que ce qui change. Les autres attelages ne sont plus un
 * obstacle : l'API les range, et le plan s'affiche AVANT d'enregistrer — c'est
 * le « ranger seul, et confirmer » choisi sur la maquette.
 */
export function BoiteChangement({
  changement,
  chauffeurs,
  remorques,
  apercu,
  envoi,
  quandChanger,
  quandFermer,
  quandValider,
}: {
  readonly changement: Changement;
  readonly chauffeurs: readonly OptionRessource[];
  readonly remorques: readonly OptionRessource[];
  readonly apercu: Apercu;
  readonly envoi: boolean;
  readonly quandChanger: (champs: Partial<Changement>) => void;
  readonly quandFermer: () => void;
  readonly quandValider: () => void;
}) {
  const liste = (
    id: string,
    etiquette: string,
    valeur: string,
    options: readonly OptionRessource[],
    quand: (valeur: string) => void,
  ) => (
    <>
      <label className="etiquette-boite" htmlFor={id}>
        {etiquette}
      </label>
      <select
        id={id}
        className="champ-boite"
        value={valeur}
        disabled={envoi}
        onChange={(e) => quand(e.target.value)}
      >
        <option value={INCHANGE}>— ne pas changer —</option>
        <option value={AUCUN}>— aucun (décrocher) —</option>
        {options.map((o) => (
          <option value={o.id} key={o.id}>
            {o.libelle}
          </option>
        ))}
      </select>
    </>
  );

  return (
    <div className="voile-boite" role="presentation">
      <div
        className="boite-decrochage boite-changement"
        role="dialog"
        aria-modal="true"
      >
        <h3>Changer l’attelage</h3>
        <p className="quoi-decroche">
          Du véhicule <strong>{changement.vehicule}</strong>. Ne remplissez que ce qui
          change : les autres attelages sont rangés automatiquement.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (planValable(apercu)) quandValider();
          }}
        >
          <div className="paire-boite">
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="changement-du">
                À partir du
              </label>
              <input
                id="changement-du"
                className="champ-boite"
                type="date"
                value={changement.du}
                disabled={envoi}
                onChange={(e) => quandChanger({ du: e.target.value })}
              />
            </div>
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="changement-au">
                Jusqu’au <span className="sourdine">(facultatif)</span>
              </label>
              <input
                id="changement-au"
                className="champ-boite"
                type="date"
                value={changement.au}
                min={changement.du}
                disabled={envoi}
                onChange={(e) => quandChanger({ au: e.target.value })}
              />
            </div>
          </div>

          <div className="bloc-liste-boite">
            {liste(
              'changement-chauffeur',
              'Chauffeur',
              changement.chauffeur,
              chauffeurs,
              (v) => quandChanger({ chauffeur: v }),
            )}
          </div>
          <div className="bloc-liste-boite">
            {liste(
              'changement-remorque',
              'Remorque',
              changement.remorque,
              remorques,
              (v) => quandChanger({ remorque: v }),
            )}
          </div>

          <BlocApercu apercu={apercu} />

          <div className="actions">
            <button
              className="bouton"
              type="submit"
              disabled={envoi || !planValable(apercu)}
            >
              {envoi ? 'Enregistrement…' : 'Enregistrer ces changements'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * CORRIGER LES DATES D'UNE PÉRIODE — lot 94. Même mécanique que le
 * changement : les périodes voisines sont rangées, et le plan se lit avant.
 */
export function BoiteCorrection({
  correction,
  apercu,
  envoi,
  quandChanger,
  quandFermer,
  quandValider,
}: {
  readonly correction: Correction;
  readonly apercu: Apercu;
  readonly envoi: boolean;
  readonly quandChanger: (champs: Partial<Correction>) => void;
  readonly quandFermer: () => void;
  readonly quandValider: () => void;
}) {
  const { ligne } = correction;
  return (
    <div className="voile-boite" role="presentation">
      <div
        className="boite-decrochage boite-changement"
        role="dialog"
        aria-modal="true"
      >
        <h3>Modifier les dates</h3>
        <p className="quoi-decroche">
          <strong>{ligne.ressource}</strong> sur {ligne.vehicule}, actuellement{' '}
          {periodeDite(ligne)}.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (planValable(apercu)) quandValider();
          }}
        >
          <div className="paire-boite">
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="correction-du">
                Premier jour
              </label>
              <input
                id="correction-du"
                className="champ-boite"
                type="date"
                value={correction.du}
                disabled={envoi}
                onChange={(e) => quandChanger({ du: e.target.value })}
              />
            </div>
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="correction-au">
                Dernier jour <span className="sourdine">(vide = en cours)</span>
              </label>
              <input
                id="correction-au"
                className="champ-boite"
                type="date"
                value={correction.au}
                min={correction.du}
                disabled={envoi}
                onChange={(e) => quandChanger({ au: e.target.value })}
              />
            </div>
          </div>

          <BlocApercu apercu={apercu} />

          <div className="actions">
            <button
              className="bouton"
              type="submit"
              disabled={envoi || !planValable(apercu)}
            >
              {envoi ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * SUPPRIMER UNE PÉRIODE — lot 94. Pour une saisie fautive : un attelage qui a
 * vraiment eu lieu se corrige, il ne s'efface pas. D'où la phrase, et le
 * bouton qui ne dit pas « OK ».
 */
export function BoiteSuppression({
  ligne,
  envoi,
  quandFermer,
  quandValider,
}: {
  readonly ligne: Affectation;
  readonly envoi: boolean;
  readonly quandFermer: () => void;
  readonly quandValider: () => void;
}) {
  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Supprimer cette période ?</h3>
        <p className="quoi-decroche">
          <strong>{ligne.ressource}</strong> sur {ligne.vehicule}, {periodeDite(ligne)}.
        </p>
        <p className="aide-boite">
          À réserver à une saisie faite par erreur : la période disparaît de
          l’historique. Si l’attelage a vraiment eu lieu, corrigez plutôt ses dates.
        </p>
        <div className="actions">
          <button
            className="bouton bouton-risque"
            type="button"
            disabled={envoi}
            onClick={quandValider}
          >
            Supprimer la période
          </button>
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={envoi}
            onClick={quandFermer}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * La demande de date, posée par-dessus l'écran.
 *
 * **« Dernier jour d'attelage » et non « décroché le »**, décidé le 04/09/2026.
 * La borne saisie est EXACTEMENT celle qui part en base : aucun calcul de veille
 * dans le dos, donc rien à deviner sur ce que la date veut dire. C'est ce calcul
 * silencieux qui rendait le décrochage du jour même impossible.
 */
function BoiteDecrochage({
  decrochage,
  envoi,
  quandAu,
  quandFermer,
  quandValider,
}: {
  readonly decrochage: Decrochage;
  readonly envoi: boolean;
  readonly quandAu: (au: string) => void;
  readonly quandFermer: () => void;
  readonly quandValider: () => void;
}) {
  const faute = refusFin(decrochage.du, decrochage.au);
  const quoi = decrochage.genre === 'chauffeur' ? 'le chauffeur' : 'la remorque';

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Décrocher {quoi}</h3>
        <p className="quoi-decroche">
          <strong>{decrochage.ressource}</strong> du véhicule {decrochage.vehicule},
          attelé depuis le {enFrancais(decrochage.du) ?? decrochage.du}.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandValider();
          }}
        >
          <label className="etiquette-boite" htmlFor="fin-attelage">
            Dernier jour d’attelage
          </label>
          <input
            id="fin-attelage"
            className="champ-boite"
            type="date"
            value={decrochage.au}
            min={decrochage.du}
            disabled={envoi}
            onChange={(e) => quandAu(e.target.value)}
          />
          {faute === null ? (
            <p className="aide-boite">
              Ce jour compris. {decrochage.genre === 'chauffeur' ? 'Il' : 'Elle'} sera
              de nouveau libre le lendemain, et l’attelage restera dans l’historique.
            </p>
          ) : (
            <p className="faute-boite">{faute}</p>
          )}
          <div className="pied-boite">
            <button className="bouton" type="submit" disabled={envoi || faute !== null}>
              {envoi ? 'Enregistrement…' : 'Décrocher'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Le filtre de l'écran, traduit pour l'API.
 *
 * La ressource y est portée par un préfixe — « v: », « r: », « c: » — parce
 * qu'un `<select>` ne rend qu'une chaîne, et que l'API attend trois paramètres
 * distincts. Un seul endroit fait la traduction.
 */
function filtreApi(filtre: FiltreHistorique): FiltreAffectations {
  const [genre, id] = filtre.ressource.split(':');
  return {
    ...(filtre.du === '' ? {} : { du: filtre.du }),
    ...(filtre.au === '' ? {} : { au: filtre.au }),
    ...(id === undefined || id === ''
      ? {}
      : genre === 'v'
        ? { vehicule: id }
        : genre === 'r'
          ? { remorque: id }
          : { chauffeur: id }),
  };
}

/**
 * Trois mois en arrière, en `AAAA-MM-JJ`.
 *
 * Calculé en UTC, comme `veille` : on n'en tire qu'une date, jamais une heure.
 * `setUTCMonth` ramène de lui-même le 31 mai à trois mois en arrière au 28
 * février — c'est le comportement voulu, et il vaut mieux que d'inventer une
 * règle de bord de mois.
 */
function ilYaTroisMois(jour: string): string {
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  if (morceaux === null) return jour;
  const date = new Date(
    Date.UTC(Number(morceaux[1]), Number(morceaux[2]) - 1, Number(morceaux[3])),
  );
  date.setUTCMonth(date.getUTCMonth() - 3);
  return date.toISOString().slice(0, 10);
}
